import React, { useCallback, useEffect, useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	LinearProgress,
	FormGroup,
	FormControlLabel,
	Typography,
} from "@mui/material";
import AddDialogStyle from "styles/application/AddDialogStyle";
import {
	patchmodelAssest,
	postModelAsset,
} from "services/models/modelDetails/modelAsset";
import EMICheckbox from "components/Elements/EMICheckbox";
import SiteAssetRegisterDropdown from "components/Elements/SiteAssetRegisterDropdown";
import DyanamicDropdown from "components/Elements/DynamicDropdown";
import {
	handleSort,
	handleValidateObj,
	generateErrorState,
} from "helpers/utils";
import {
	getSiteAssetRegistersLookup,
	getSiteAssetRegistersLookupCount,
} from "services/clients/sites/siteAssetRegisters";
import * as yup from "yup";
import ErrorInputFieldWrapper from "components/Layouts/ErrorInputFieldWrapper";
import { addSiteAsset } from "services/clients/sites/siteAssets";
import { isChrome } from "helpers/utils";
import { showError } from "../../../../redux/common/actions";
import { useDispatch } from "react-redux";
import { FULL_ACCESS } from "constants/AccessTypes/AccessTypes";

const ADD = AddDialogStyle();

const initialInput = {
	asset: {},
	status: true,
	name: "",
	description: "",
	modelVersionArrangementID: "",
};

// Yup validation schema
const schema = (serviceAcces, assetID, hideArrangements) =>
	yup.object({
		asset: yup
			.string("Asset is required")
			.nullable()
			.when("assetname", {
				is: () => serviceAcces !== FULL_ACCESS,
				then: () =>
					yup.string("Asset is required").required("Asset is required"),
			}),
		name: yup
			.string("This field is required")
			.nullable()
			.when("asset", {
				is: () =>
					(serviceAcces === FULL_ACCESS && assetID === null) ||
					(serviceAcces === FULL_ACCESS && assetID === undefined),
				then: () => yup.string("Name is required").required("Name is required"),
			}),
		description: yup
			.string()
			.max(255, "Must be less than or equal to 255 characters ")
			.when('name', {
				is: (name) => name && name.length > 0,
				then: () => yup.string().required("Description is required"),
				otherwise: () => yup.string().nullable()
			}),
		status: yup.bool().required(),
		modelVersionArrangementID: hideArrangements
			? yup.string().nullable()
			: yup.string().required("Arrangement is required"),
	});
const debounce = (func, delay) => {
	let timer;
	return function () {
		let self = this;
		let args = arguments;
		clearTimeout(timer);
		timer = setTimeout(() => {
			func.apply(self, args);
		}, delay);
	};
};

const AddModel = ({
	open,
	handleClose,
	modelId,
	getError,
	title,
	handleAddComplete,
	editData,
	fetchModelAsset,
	isEdit = false,
	serviceAccess,
	isFromService = false,
	handleServiceAddComplete,
	arrangementTitle,
	hideArrangements = true,
	arrangementDatas,
	showAssetReferences,
	customCaptions,
}) => {
	const [loading, setLoading] = useState(false);
	const [assets, setAsset] = useState([]);
	const [input, setInput] = useState(initialInput);
	const [page, setPage] = useState({ pageNo: 1, pageSize: 10 });
	const [count, setCount] = useState(0);
	const { position, siteID } =
		JSON.parse(sessionStorage.getItem("me")) ||
		JSON.parse(localStorage.getItem("me"));
	const [errors, setErrors] = useState({
		asset: null,
		status: null,
		name: null,
		description: null,
		modelVersionArrangementID: null,
	});

	const [modelFocus, setModelFocus] = useState(true);
	const dispatch = useDispatch();
	
	const isAssetSelected = input.asset?.id !== undefined;
	const isManualEntryMode = input.name?.length > 0 || input.description?.length > 0;

	useEffect(() => {
		if (open) {
			fetchAssetData();
		}
	}, [open]);

	useEffect(() => {
		if (arrangementDatas?.length === 1) {
			setInput((prev) => ({
				...prev,
				modelVersionArrangementID: arrangementDatas[0],
			}));
		}
	}, [arrangementDatas]);

	useEffect(() => {
		if (isEdit && editData) {
			const assetData = {
				id: editData?.siteAssetRegisterID,
				assetNumber: editData?.assetNumber,
				reference: editData?.assetReferenceName,
				description: editData?.description || "",
			};
			setInput({
				asset: assetData,
				status: editData?.isActive,
				name: "",
				description: "",
			});
		}
	}, [editData, isEdit, showAssetReferences]);

	const fetchAssets = async (pNo = 1, prevData = [], sortField = "", sortOrder = "") => {
		try {
			let result = await getSiteAssetRegistersLookup(
				null,
				position?.siteAppID,
				pNo,
				page.pageSize,
				"",
				sortField,
				sortOrder
			);
			if (result.status) {
				setAsset([...prevData, ...result.data]);
			}
		} catch (e) {
			dispatch(showError("Failed to load assets"));
		}
	};

	const fetchAssetCount = async () => {
		try {
			let result = await getSiteAssetRegistersLookupCount(siteID, null);
			if (result.status) {
				setCount(result.data);
			}
		} catch (e) {
			dispatch(showError("Failed to get asset count"));
		}
	};

	const fetchAssetData = async () => {
		setLoading(true);
		try {
			await Promise.all([fetchAssets(), fetchAssetCount()]);
		} catch (error) {
			dispatch(showError("Failed to load assets"));
		} finally {
			setLoading(false);
		}
	};

	const addServiceModelAsset = async () => {
		if (input.name && !input.description) {
			setErrors({
				...errors,
				description: "Description is required"
			});
			return;
		}

		const { status, asset, name, description, modelVersionArrangementID } = input;

		const localChecker = await handleValidateObj(
			schema(serviceAccess, asset.id, hideArrangements),
			{
				asset: asset?.id,
				status,
				name,
				description,
				modelVersionArrangementID: modelVersionArrangementID?.id,
			}
		);

		if (!localChecker.some((el) => el.valid === false)) {
			const formattedData = {
				name: asset?.assetNumber || asset?.name || name,
				assetReferenceName: asset?.reference || asset?.assetReferenceName,
				description: asset?.description || description,
				modelVersionArrangementID: modelVersionArrangementID
			};
			
			handleServiceAddComplete(formattedData);
			setInput(initialInput);
		} else {
			const newError = generateErrorState(localChecker);
			setErrors({ ...errors, ...newError });
		}
	};

	const addModelAsset = async () => {
		setLoading(true);

		const { status, asset, name, description, modelVersionArrangementID } = input;

		if (name && !description) {
			setErrors({
				...errors,
				description: "Description is required"
			});
			setLoading(false);
			return;
		}

		const data = {
			ModelID: +modelId,
			SiteAssetRegisterID: asset.id,
			isActive: status,
			modelVersionArrangementID: modelVersionArrangementID?.id || "",
		};

		try {
			const localChecker = await handleValidateObj(
				schema(serviceAccess, asset.id, hideArrangements, name),
				{
					asset: asset?.id,
					status,
					name,
					description,
					modelVersionArrangementID: input?.modelVersionArrangementID?.id,
				}
			);
			if (!localChecker.some((el) => el.valid === false)) {
				if (!isEdit) {
					let siteAsset;
					if (serviceAccess === FULL_ACCESS) {
						if (
							asset.id === "" ||
							asset.id === undefined ||
							asset.id === null
						) {
							siteAsset = await addSiteAsset({ siteID, name, description });
							if (!siteAsset.status) {
								getError(siteAsset.data.detail || "Failed To add asset");
								setLoading(false);
								return;
							}
						}
					}
					let result = await postModelAsset(
						serviceAccess === FULL_ACCESS && asset.id === undefined && name !== ""
							? { ...data, SiteAssetRegisterID: siteAsset.data }
							: data
					);
					if (result.status) {
						handleAddComplete({
							...input,
							isActive: input.status ? true : false,
						});
						await fetchModelAsset(false);
						closeOverride();
					} else {
						if (result.data?.detail) getError(result.data.detail);
						else {
							if (result.data?.detail) getError(result.data.detail);
						}
					}
				} else {
					let result = await patchmodelAssest(editData?.id, [
						{
							op: "replace",
							path: "isActive",
							value: status,
						},
					]);
					if (result.status) {
						await fetchModelAsset(false);
						closeOverride();
					} else {
						getError(result.data.detail);
					}
				}
			} else {
				const newError = generateErrorState(localChecker);
				setErrors({ ...errors, ...newError });
			}
		} catch (e) {
			dispatch(showError(`Failed to add ${title}.`));
		}
		setLoading(false);
	};

	const closeOverride = () => {
		handleClose();
		setAsset([]);
		setErrors({
			asset: null,
			status: null,
			name: null,
			description: null,
			modelVersionArrangementID: null,
		});
		setPage({ pageNo: 1, pageSize: 10 });
		!isEdit && setInput({ asset: {}, status: true, description: "", name: "" });
	};

	const pageChange = async (p, prevData) => {
		setLoading(true);
		try {
			await fetchAssets(p, prevData);
			setPage({ pageNo: p, pageSize: page.pageSize });
		} catch (error) {
			dispatch(showError("Failed to load more assets"));
		} finally {
			setLoading(false);
		}
	};

	const handleServerSideSearch = useCallback(
		debounce(async (searchTxt) => {
			if (searchTxt) {
				setLoading(true);
				try {
					const response = await getSiteAssetRegistersLookup(
						null,
						position.siteAppID,
						1,
						20,
						searchTxt
					);
					if (response?.status) {
						setAsset(response.data);
					}
				} catch (error) {
					dispatch(showError("Failed to search assets"));
				} finally {
					setLoading(false);
				}
			} else {
				pageChange(1, []);
			}
		}, 500),
		[position.siteAppID, dispatch]
	);

	const handleAssetChange = (val) => {
		setInput((th) => ({
			...th,
			asset: val ? val : {},
			name: "",
			description: "",
		}));
	};

	const handleAssetClear = () => {
		setInput((th) => ({
			...th,
			asset: {},
			name: "",
			description: "",
		}));
	};
	
	const handleNameChange = (e) => {
		if (errors.description) {
			setErrors({
				...errors,
				description: null
			});
		}
		
		setInput({
			...input,
			name: e.target.value,
			asset: {},
		});
	};
	
	const handleDescriptionChange = (e) => {
		if (errors.description) {
			setErrors({
				...errors,
				description: null
			});
		}
		
		setInput({
			...input,
			description: e.target.value,
			asset: {},
		});
	};

	return (
		<Dialog
			open={open}
			onClose={closeOverride}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			className="application-dailog"
			disableEnforceFocus={isChrome() ? modelFocus : false}
		>
			{loading ? <LinearProgress /> : null}
			<ADD.ActionContainer>
				<DialogTitle>
					<ADD.HeaderText>
						{isEdit ? "Edit" : "Add"} {title}
					</ADD.HeaderText>
				</DialogTitle>
				<ADD.ButtonContainer>
					<div className="modalButton">
						<ADD.CancelButton
							onClick={closeOverride}
							variant="contained"
							onFocus={(e) => {
								setModelFocus(true);
							}}
						>
							Cancel
						</ADD.CancelButton>
					</div>
					<div className="modalButton">
						<ADD.ConfirmButton
							onClick={isFromService ? addServiceModelAsset : addModelAsset}
							variant="contained"
						>
							{isEdit ? "Close" : `Add ${title}`}
						</ADD.ConfirmButton>
					</div>
				</ADD.ButtonContainer>
			</ADD.ActionContainer>
			<Divider />
			<DialogContent>
				<div
					style={{
						display: "flex",
						justifyContent: isEdit ? "left" : "space-between",
						alignItems: "center",
						width: "100%"
					}}
				>
					{!isEdit && (
						<ErrorInputFieldWrapper
							errorMessage={errors?.asset === null ? null : errors?.asset}
							style={{ 
								width: isFromService ? "100%" : "80%", 
								flex: "1", 
								minWidth: "250px"
							}}
						>
							<SiteAssetRegisterDropdown
								label={title}
								dataSource={assets}
								selectedValue={input.asset?.id ? input.asset : null}
								onChange={handleAssetChange}
								onClear={handleAssetClear}
								count={count}
								customCaptions={customCaptions}
								showReference={showAssetReferences}
								required={true}
								isError={errors?.asset ? true : false}
								disabled={isManualEntryMode && !isAssetSelected}
								onPageChange={pageChange}
								page={page}
								handleServerSideSearch={handleServerSideSearch}
								isServerSide={true}
								fetchData={fetchAssetData}
							/>
						</ErrorInputFieldWrapper>
					)}

					{!isFromService && (
						<FormGroup style={{ marginLeft: "20px", flexShrink: 0, width: "auto" }}>
							<FormControlLabel
								control={
									<EMICheckbox
										state={input?.status}
										changeHandler={() => {
											setInput((th) => ({
												...th,
												status: !th.status,
											}));
										}}
									/>
								}
								label={<Typography>Active</Typography>}
							/>
						</FormGroup>
					)}
				</div>
				{serviceAccess === FULL_ACCESS && (
					<>
						{!showAssetReferences && (
							<>
								<p style={{ textAlign: "center", margin: "16px 0 8px 0" }}>Or</p>
								<div>
									<ADD.NameLabel>
										Name{!isAssetSelected && <ADD.RequiredStar>*</ADD.RequiredStar>}
									</ADD.NameLabel>
									<ErrorInputFieldWrapper
										errorMessage={errors?.name === null ? null : errors?.name}
									>
										<ADD.NameInput
											error={errors.name === null ? false : true}
											variant="outlined"
											size="medium"
											value={input.name}
											disabled={isAssetSelected}
											onKeyDown={() => {}}
											onChange={handleNameChange}
											style={{
												backgroundColor: isAssetSelected ? '#f5f5f5' : 'inherit',
												opacity: isAssetSelected ? 0.7 : 1
											}}
										/>
									</ErrorInputFieldWrapper>
								</div>

								<div style={{ marginTop: "20px" }}>
									<ADD.NameLabel>
										Description
										{input.name && !isAssetSelected && <ADD.RequiredStar>*</ADD.RequiredStar>}
									</ADD.NameLabel>
									<ErrorInputFieldWrapper
										errorMessage={
											input.name?.length > 0 && !input.description
												? "Description is required"
												: errors?.description
										}
									>
										<ADD.NameInput
											error={
												(input.name?.length > 0 && !input.description) ||
												errors.description !== null
											}
											variant="outlined"
											size="medium"
											multiline
											value={input.description}
											disabled={isAssetSelected}
											onKeyDown={() => {}}
											onChange={handleDescriptionChange}
											onBlur={() => {
												setModelFocus(false);
											}}
											style={{
												backgroundColor: isAssetSelected ? '#f5f5f5' : 'inherit',
												opacity: isAssetSelected ? 0.7 : 1
											}}
										/>
									</ErrorInputFieldWrapper>
								</div>
							</>
						)}
						
						{!hideArrangements && (
							<>
								<Divider style={{ margin: "20px 0 16px 0" }} />
								<div>
									<ErrorInputFieldWrapper
										errorMessage={
											errors?.modelVersionArrangementID === null
												? null
												: errors?.modelVersionArrangementID
										}
									>
										<DyanamicDropdown
											dataSource={arrangementDatas}
											isServerSide={false}
											width="100%"
											placeholder={`Select ${arrangementTitle}`}
											dataHeader={[{ id: 1, name: "Interval" }]}
											columns={[{ id: 1, name: "name" }]}
											selectedValue={input["modelVersionArrangementID"]}
											handleSort={handleSort}
											onChange={(val) => {
												setInput({
													...input,
													modelVersionArrangementID: val,
												});
											}}
											selectdValueToshow="name"
											label={arrangementTitle}
											required
										/>
									</ErrorInputFieldWrapper>
								</div>
							</>
						)}
					</>
				)}
			</DialogContent>
		</Dialog>
	);
};
export default AddModel;
